/* Shopify owns contact delivery and Google tag loading. Only a confirmed native
 * response with a matching same-tab reference can attempt a laser conversion.
 * Queued events are attempts, not provider delivery receipts. */
(() => {
  const PENDING = 'hok.laser.inquiry.pending.v1';
  const ATTRIBUTION = 'hok.laser.attribution.v1';
  const TTL = 30 * 60 * 1000;
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const budgets = { 'Around $5,495': 'mid', 'Around $12,495': 'premium', 'Compare both': 'compare', 'Not sure yet': 'undecided' };
  const settings = { 'Commercial property': 'facilities', 'Vineyard or crop operation': 'vineyard', 'Pond, hatchery or animal operation': 'pond-animal', 'Other commercial site': 'other' };
  const rules = {
    utm_source: /^google$/, utm_medium: /^(cpc|ppc|paid-search)$/,
    utm_campaign: /^laser(?:[-_][a-z0-9]+){1,12}$/,
    utm_content: /^(facilities|vineyard|solar|product|general|\d{1,20})$/,
    utm_id: /^\d{1,20}$/, campaignid: /^\d{1,20}$/, adgroupid: /^\d{1,20}$/, creative: /^\d{1,20}$/,
    device: /^[cmt]$/, network: /^[gsdxy]$/,
    gclid: /^[A-Za-z0-9_-]{8,256}$/, gbraid: /^[A-Za-z0-9_-]{8,256}$/, wbraid: /^[A-Za-z0-9_-]{8,256}$/
  };
  const clickKeys = ['gclid', 'gbraid', 'wbraid'];
  const read = key => { try { return JSON.parse(window.sessionStorage.getItem(key)); } catch { return null; } };
  const write = (key, value) => {
    try {
      if (value) window.sessionStorage.setItem(key, JSON.stringify(value));
      else window.sessionStorage.removeItem(key);
      return true;
    } catch { return false; }
  };
  const fresh = at => Number.isFinite(at) && Date.now() >= at && Date.now() - at <= TTL;
  const permission = method => { try { return window.Shopify?.customerPrivacy?.[method]?.() === true; } catch { return false; } };
  const attributionFrom = (params, allowClicks) => {
    const safe = {};
    for (const [key, rule] of Object.entries(rules)) {
      const value = params.get(key);
      if ((!clickKeys.includes(key) || allowClicks) && typeof value === 'string' && rule.test(value)) safe[key] = value;
    }
    return safe;
  };
  const initialize = marker => {
    if (marker.dataset.laserMeasured === 'true') return;
    marker.dataset.laserMeasured = 'true';
    const form = marker.closest('form');
    if (!form || form.dataset.reviewOnly === 'true') return;
    const state = marker.dataset.laserServerStatus;
    const params = new URLSearchParams(window.location.search);
    const returnedReference = params.get('hok_inquiry_ref');
    const preview = marker.dataset.laserReview !== 'false' || window.Shopify?.theme?.role !== 'main' ||
      Boolean(window.Shopify?.designMode) || params.has('preview_theme_id');
    const gaDestination = /^G-[A-Z0-9]+$/.test(marker.dataset.laserGaDestination || '') ? marker.dataset.laserGaDestination : '';
    const gaEvent = marker.dataset.laserGaEvent === 'laser_bird_site_inquiry' ? marker.dataset.laserGaEvent : '';
    const adsDestination = /^AW-\d+\/[A-Za-z0-9_-]+$/.test(marker.dataset.laserAdsDestination || '') ? marker.dataset.laserAdsDestination : '';
    const receipt = { version: 1, server: state, tracking: 'awaiting-submission', attribution: 'not-captured', ga: preview ? 'review-suppressed' : 'not-attempted', ads: preview ? 'review-suppressed' : 'not-attempted' };
    const report = () => {
      receipt.tagAvailable = typeof window.gtag === 'function';
      receipt.privacyApiAvailable = Boolean(window.Shopify?.customerPrivacy);
      receipt.reviewContext = preview;
      receipt.themeRole = ['main', 'unpublished', 'development'].includes(window.Shopify?.theme?.role) ? window.Shopify.theme.role : 'unknown';
      marker.dataset.laserTracking = receipt.tracking;
      marker.dataset.laserGaStatus = receipt.ga;
      marker.dataset.laserAdsStatus = receipt.ads;
      marker.textContent = JSON.stringify(receipt);
    };
    const valid = pending => Boolean(pending && pending.form === form.id && UUID.test(pending.id) && fresh(pending.at) &&
      ['undecided', ...Object.values(budgets)].includes(pending.budget) && ['undecided', ...Object.values(settings)].includes(pending.setting) &&
      typeof pending.ga === 'boolean' && typeof pending.ads === 'boolean' && typeof pending.review === 'boolean');
    let landingConsumed = false;
    let hadMarketingPermission = false;
    const syncAttribution = () => {
      const field = form.querySelector('[data-laser-attribution-field]');
      if (field) field.value = '';
      if (!window.Shopify?.customerPrivacy) { receipt.attribution = 'privacy-api-unavailable'; return; }
      if (!permission('marketingAllowed')) {
        if (hadMarketingPermission) landingConsumed = true;
        write(ATTRIBUTION, null);
        receipt.attribution = 'consent-not-allowed';
        return;
      }
      hadMarketingPermission = true;
      const allowClicks = permission('saleOfDataAllowed');
      let saved = read(ATTRIBUTION);
      if (!landingConsumed && state !== 'success') {
        landingConsumed = true;
        const current = attributionFrom(params, allowClicks);
        if (Object.keys(current).length) {
          // One latest landing touch: never combine a new campaign with an old click ID.
          const candidate = { at: Date.now(), data: current };
          saved = write(ATTRIBUTION, candidate) ? candidate : null;
        }
      }
      if (saved && fresh(saved.at) && saved.data && typeof saved.data === 'object' && !Array.isArray(saved.data)) {
        const safe = attributionFrom(new URLSearchParams(saved.data), allowClicks);
        if (Object.keys(safe).length && write(ATTRIBUTION, { at: saved.at, data: safe })) {
          if (field) field.value = JSON.stringify(safe);
          receipt.attribution = 'captured-with-marketing-permission';
          return;
        }
      }
      write(ATTRIBUTION, null);
      receipt.attribution = 'not-captured';
    };
    if (state === 'error') {
      const pending = read(PENDING);
      if (valid(pending)) {
        // Shopify retains contact values where supported. Restore only missing fixed
        // selections from our pending record; never store or replay contact details.
        for (const [selector, values, selected] of [
          ['[data-laser-budget-field]', budgets, pending.budget],
          ['[data-laser-context-field]', settings, pending.setting]
        ]) {
          const field = form.querySelector(selector);
          const value = Object.keys(values).find(key => values[key] === selected);
          if (field && !field.value && value) { field.value = value; field.dispatchEvent(new Event('change', { bubbles: true })); }
        }
      }
      write(PENDING, null);
      receipt.tracking = 'server-error';
    }
    if (state === 'ready' || state === 'error') {
      form.addEventListener('submit', event => {
        if (!marker.isConnected || event.defaultPrevented || !form.checkValidity()) return;
        const reference = form.querySelector('[data-laser-inquiry-reference]');
        const returnTo = form.querySelector('input[name="return_to"]');
        if (reference) reference.value = '';
        if (returnTo) returnTo.value = 'back';
        syncAttribution();
        const analytics = permission('analyticsProcessingAllowed');
        const ads = permission('marketingAllowed') && permission('saleOfDataAllowed');
        if (!analytics && !ads) {
          write(PENDING, null); receipt.tracking = 'measurement-consent-not-allowed'; report(); return;
        }
        let id;
        try { id = window.crypto.randomUUID(); } catch { receipt.tracking = 'reference-unavailable'; report(); return; }
        if (!reference || !returnTo || !UUID.test(id)) { receipt.tracking = 'native-reference-route-unavailable'; report(); return; }
        const pending = {
          id, form: form.id, at: Date.now(), review: preview,
          budget: budgets[form.querySelector('[data-laser-budget-field]')?.value] || 'undecided',
          setting: settings[form.querySelector('[data-laser-context-field]')?.value] || 'undecided', ga: false, ads: false
        };
        if (!write(PENDING, pending)) { receipt.tracking = 'storage-unavailable'; report(); return; }
        reference.value = id;
        const returnParams = new URLSearchParams();
        if (params.get('view') === 'laser-bird') returnParams.set('view', 'laser-bird');
        if (/^\d+$/.test(params.get('preview_theme_id') || '')) returnParams.set('preview_theme_id', params.get('preview_theme_id'));
        // Shopify needs this flag to render native success after an explicit
        // return_to. The fresh same-tab reference still binds measurement.
        returnParams.set('contact_posted', 'true');
        returnParams.set('hok_inquiry_ref', id);
        returnTo.value = `${window.location.pathname}?${returnParams.toString()}#${form.id}`;
        receipt.reference = id; receipt.tracking = 'submission-pending'; report();
        // Never preventDefault/fetch: native Shopify validation, challenge and delivery remain intact.
      });
    }
    let timer;
    let attempts = 0;
    let privacyRequested = false;
    const update = () => {
      window.clearTimeout(timer);
      if (!marker.isConnected) return;
      syncAttribution();
      if (!permission('analyticsProcessingAllowed') && !(permission('marketingAllowed') && permission('saleOfDataAllowed')) && window.Shopify?.customerPrivacy) write(PENDING, null);
      if (state !== 'success') { report(); return; }
      const pending = read(PENDING);
      if (!valid(pending) || returnedReference !== pending.id) {
        receipt.tracking = 'no-matching-confirmed-submission'; report(); return;
      }
      receipt.reference = pending.id;
      const review = preview || pending.review;
      const tagReady = typeof window.gtag === 'function';
      receipt.ga = review ? 'review-suppressed' : pending.ga ? 'already-attempted' : !gaDestination || !gaEvent ? 'disabled' :
        !permission('analyticsProcessingAllowed') ? 'consent-not-allowed' : !tagReady ? 'tag-unavailable' : 'eligible';
      receipt.ads = review ? 'review-suppressed' : pending.ads ? 'already-attempted' : !adsDestination ? 'disabled' :
        !(permission('marketingAllowed') && permission('saleOfDataAllowed')) ? 'consent-not-allowed' : !tagReady ? 'tag-unavailable' : 'eligible';
      const ga = receipt.ga === 'eligible';
      const ads = receipt.ads === 'eligible';
      receipt.tracking = 'server-confirmed';
      if (ga || ads) {
        // Persist before calling the existing tag: refresh, back/forward and consent
        // callbacks cannot repeat this attempt, even if the tag throws.
        if (!write(PENDING, { ...pending, ga: pending.ga || ga, ads: pending.ads || ads })) {
          receipt.tracking = 'confirmed-storage-unavailable'; report(); return;
        }
        const context = { page_location: window.location.origin + window.location.pathname, page_referrer: '', page_title: 'Commercial Laser Bird Deterrence | House of Koval' };
        if (ga) {
          try {
            window.gtag('event', gaEvent, { send_to: gaDestination, inquiry_id: pending.id, product_family: 'laser_bird_deterrent',
              planning_budget: pending.budget, site_type: pending.setting, ...context });
            receipt.ga = 'queued';
          } catch { receipt.ga = 'tag-call-failed-no-retry'; }
        }
        if (ads) {
          try {
            window.gtag('config', adsDestination.split('/')[0], { send_page_view: false });
            window.gtag('event', 'conversion', { send_to: adsDestination, transaction_id: pending.id, ...context });
            receipt.ads = 'queued';
          } catch { receipt.ads = 'tag-call-failed-no-retry'; }
        }
        receipt.tracking = 'events-attempted';
      }
      report();
      if ([receipt.ga, receipt.ads].some(value => ['tag-unavailable', 'consent-not-allowed'].includes(value)) && ++attempts < 30) timer = window.setTimeout(update, 1000);
    };
    const loadPrivacy = () => {
      if (!window.Shopify?.customerPrivacy && !privacyRequested && typeof window.Shopify?.loadFeatures === 'function') {
        privacyRequested = true;
        try { window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => { if (!error) update(); }); } catch { /* Missing privacy API stays closed. */ }
      }
    };
    document.addEventListener('visitorConsentCollected', update);
    window.addEventListener('pageshow', update);
    update(); loadPrivacy();
  };
  document.querySelectorAll('[data-laser-inquiry-form]').forEach(initialize);
  document.addEventListener('shopify:section:load', event => {
    event.target.querySelectorAll('[data-laser-inquiry-form]').forEach(initialize);
  });
})();
